import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { AnyRow } from "@/lib/rowTypes";

export interface CourseProgress {
  courseName: string;
  totalLessons: number;
  completedLessons: number;
  isComplete: boolean;
  phase: number;
  phaseLabel: string;
  minOrder: number;
  maxOrder: number;
}

export type ModuleReadiness = "live" | "beta" | "coming_soon" | "hidden";

export interface ModuleGate {
  moduleKey: string;
  moduleName: string;
  route: string;
  prerequisites: string[];
  recommendedCourses?: string[];
  isUnlocked: boolean;
  isCompleted: boolean;
  missingPrereqs: string[];
  unlockRequirement: string | null;
  description: string;
  phase: number;
  /** Source-of-truth readiness from module_definitions.readiness */
  readiness: ModuleReadiness;
}

type LessonRow = {
  lesson_id: string;
  course_name: string;
  lesson_order: number;
};

const PHASE_MAP: Record<string, { phase: number; label: string }> = {
  // Tier 1 — Core Money Control
  "Financial Confidence System": { phase: 1, label: "Core Money Control" },
  "Financial Foundations": { phase: 1, label: "Core Money Control" },
  "Banking & Cash Management": { phase: 1, label: "Core Money Control" },
  "Saving & Budgeting": { phase: 1, label: "Core Money Control" },
  "Credit & Debt Management": { phase: 1, label: "Core Money Control" },
  // Tier 2 — Behavior, Stability & Life Systems
  "Behavioral Finance": { phase: 2, label: "Behavior, Stability & Life Systems" },
  "Career & Income Strategy": { phase: 2, label: "Behavior, Stability & Life Systems" },
  "Insurance & Risk Protection": { phase: 2, label: "Behavior, Stability & Life Systems" },
  "Taxes": { phase: 2, label: "Behavior, Stability & Life Systems" },
  "Transportation & Vehicle Ownership": { phase: 2, label: "Behavior, Stability & Life Systems" },
  "Family Finance": { phase: 2, label: "Behavior, Stability & Life Systems" },
  "Financial Scams & Fraud Awareness": { phase: 2, label: "Behavior, Stability & Life Systems" },
  "Financial Recovery": { phase: 2, label: "Behavior, Stability & Life Systems" },
  // Tier 3 — Economics & Systems
  "Economic Foundations": { phase: 3, label: "Economics & Systems" },
  "Microeconomics": { phase: 3, label: "Economics & Systems" },
  "Market Structures & Competition": { phase: 3, label: "Economics & Systems" },
  "Labor Economics": { phase: 3, label: "Economics & Systems" },
  "Macroeconomic Foundations": { phase: 3, label: "Economics & Systems" },
  "Money, Banking & Central Banking": { phase: 3, label: "Economics & Systems" },
  "Fiscal Policy & Government Economics": { phase: 3, label: "Economics & Systems" },
  "International Economics": { phase: 3, label: "Economics & Systems" },
  "Behavioral Economics": { phase: 3, label: "Economics & Systems" },
  "Economic Development & Institutions": { phase: 3, label: "Economics & Systems" },
  "Game Theory & Strategic Interaction": { phase: 3, label: "Economics & Systems" },
  "Economic History": { phase: 3, label: "Economics & Systems" },
  "Economic Systems in Practice": { phase: 3, label: "Economics & Systems" },
  // Tier 4 — Wealth Building & Allocation
  "Investing Fundamentals": { phase: 4, label: "Wealth Building & Allocation" },
  "Retirement & Investment Accounts": { phase: 4, label: "Wealth Building & Allocation" },
  "Real Estate (Homeownership)": { phase: 4, label: "Wealth Building & Allocation" },
  "Real Estate Investing": { phase: 4, label: "Wealth Building & Allocation" },
  "Advanced Financial Strategy": { phase: 4, label: "Wealth Building & Allocation" },
  // Tier 5 — Entrepreneurship & Business Ownership
  "Entrepreneurial Foundations": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Opportunity Identification": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Offer Creation & Validation": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Sales & Marketing Fundamentals": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Business Model Design": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Operations & Execution": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Entrepreneurial Finance": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Growth & Scale": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Founder Decision-Making": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Ownership, Equity & Exit Strategy": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Business Fundamentals": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Startups & Entrepreneurship": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  "Business & Startup Expansion": { phase: 5, label: "Entrepreneurship & Business Ownership" },
  // Tier 6 — Advanced Application & Case-Based Thinking
  "Real Estate Development": { phase: 6, label: "Advanced Application & Case Studies" },
  "Company Case Studies": { phase: 6, label: "Advanced Application & Case Studies" },
};

const HIDDEN_COURSES = new Set([
  "Company Case Studies",
  // FCI / Financial Confidence System is retired from the user-facing curriculum.
  "Financial Confidence System",
]);

const MODULE_DEFINITIONS: Omit<ModuleGate, "isUnlocked" | "isCompleted" | "missingPrereqs" | "unlockRequirement" | "readiness">[] = [
  // Tier 1 — Core Money Control
  {
    moduleKey: "account_setup",
    moduleName: "Account Setup Module",
    route: "/account-setup",
    prerequisites: [],
    description: "Learn where your money lives — Checking, Savings, and Emergency Fund.",
    phase: 1,
  },
  {
    moduleKey: "budget_builder",
    moduleName: "Budget Builder Simulator",
    route: "/budget-builder",
    prerequisites: ["Banking & Cash Management", "Saving & Budgeting"],
    description: "Build a monthly budget, assign categories, and handle surprise expenses.",
    phase: 1,
  },
  {
    moduleKey: "credit_management",
    moduleName: "Debt Payoff Simulator",
    route: "/credit-management",
    prerequisites: ["Credit & Debt Management"],
    description: "Prioritize debts, choose payoff strategies, and escape interest traps.",
    phase: 1,
  },
  // Tier 2 — Behavior, Stability & Life Systems
  {
    moduleKey: "career_path",
    moduleName: "Career Strategy Simulator",
    route: "/career-path",
    prerequisites: ["Career & Income Strategy"],
    description: "Stack skills, negotiate compensation, and build career leverage.",
    phase: 2,
  },
  {
    moduleKey: "vehicle_ownership",
    moduleName: "Vehicle Ownership Simulator",
    route: "/vehicle-ownership",
    prerequisites: ["Transportation & Vehicle Ownership"],
    description: "Choose a car, understand loans, insurance, and total ownership costs.",
    phase: 2,
  },
  {
    moduleKey: "risk_protection",
    moduleName: "Risk Protection Module",
    route: "/risk-protection",
    prerequisites: ["Insurance & Risk Protection"],
    description: "Choose coverage levels and manage deductible vs premium tradeoffs.",
    phase: 2,
  },
  {
    moduleKey: "family_budget",
    moduleName: "Family Budget & Planning Module",
    route: "/family-budget",
    prerequisites: ["Family Finance"],
    description: "Balance household priorities and plan for dependents.",
    phase: 2,
  },
  {
    moduleKey: "recovery_mode",
    moduleName: "Recovery Mode Module",
    route: "/recovery-mode",
    prerequisites: ["Financial Recovery"],
    description: "Stabilize after setbacks, preserve cash, and rebuild.",
    phase: 2,
  },
  {
    moduleKey: "scam_defense",
    moduleName: "Scam Detection Simulator",
    route: "/scam-defense",
    prerequisites: ["Financial Scams & Fraud Awareness", "Behavioral Finance"],
    description: "Identify scams, recognize manipulation, and protect your identity.",
    phase: 2,
  },
  // Tier 3 — Economics & Systems
  {
    moduleKey: "resource_allocation",
    moduleName: "Resource Allocation Simulator",
    route: "/econ/resource-allocation",
    prerequisites: ["Economic Foundations"],
    description: "Manage limited resources across competing priorities using scarcity, tradeoffs, and marginal thinking.",
    phase: 3,
  },
  {
    moduleKey: "market_dynamics",
    moduleName: "Market Dynamics Simulator",
    route: "/econ/market-dynamics",
    prerequisites: ["Microeconomics"],
    description: "Manipulate supply, demand, taxes, and price controls to visualize market outcomes.",
    phase: 3,
  },
  {
    moduleKey: "industry_competition",
    moduleName: "Industry Competition Simulator",
    route: "/econ/industry-competition",
    prerequisites: ["Market Structures & Competition"],
    description: "Compete in monopoly, oligopoly, and competitive market environments.",
    phase: 3,
  },
  {
    moduleKey: "career_market",
    moduleName: "Career Market Simulator",
    route: "/econ/career-market",
    prerequisites: ["Labor Economics"],
    description: "Navigate specialization, relocation, and industry-switching decisions.",
    phase: 3,
  },
  {
    moduleKey: "macro_dashboard",
    moduleName: "Macro Dashboard Simulator",
    route: "/econ/macro-dashboard",
    prerequisites: ["Macroeconomic Foundations"],
    description: "Interpret economic indicators and identify recession, expansion, or inflation conditions.",
    phase: 3,
  },
  {
    moduleKey: "banking_system",
    moduleName: "Banking System Simulator",
    route: "/econ/banking-system",
    prerequisites: ["Money, Banking & Central Banking"],
    description: "Simulate bank lending, reserve pressure, and confidence shocks.",
    phase: 3,
  },
  {
    moduleKey: "policy_tradeoff",
    moduleName: "Policy Tradeoff Simulator",
    route: "/econ/policy-tradeoff",
    prerequisites: ["Fiscal Policy & Government Economics"],
    description: "Balance tax policy, transfers, infrastructure, and economic stability.",
    phase: 3,
  },
  {
    moduleKey: "global_trade",
    moduleName: "Global Trade Simulator",
    route: "/econ/global-trade",
    prerequisites: ["International Economics"],
    description: "Simulate tariffs, supply chain shocks, and exchange rate dynamics.",
    phase: 3,
  },
  {
    moduleKey: "choice_architecture",
    moduleName: "Choice Architecture Lab",
    route: "/econ/choice-architecture",
    prerequisites: ["Behavioral Economics"],
    description: "Design choice environments using framing, defaults, and incentives.",
    phase: 3,
  },
  {
    moduleKey: "nation_builder",
    moduleName: "Nation Builder Simulator",
    route: "/econ/nation-builder",
    prerequisites: ["Economic Development & Institutions"],
    description: "Allocate resources and reform institutions to drive national development.",
    phase: 3,
  },
  {
    moduleKey: "strategy_arena",
    moduleName: "Strategy Arena",
    route: "/econ/strategy-arena",
    prerequisites: ["Game Theory & Strategic Interaction"],
    description: "Make repeated strategic decisions with signaling, cooperation, and commitment.",
    phase: 3,
  },
  {
    moduleKey: "crisis_timeline",
    moduleName: "Crisis Timeline Simulator",
    route: "/econ/crisis-timeline",
    prerequisites: ["Economic History"],
    description: "Walk through major economic crises and make interpretation and response choices.",
    phase: 3,
  },
  // Tier 4 — Wealth Building & Ownership
  {
    moduleKey: "market_simulator",
    moduleName: "Investing Simulator",
    route: "/simulator",
    prerequisites: ["Investing Fundamentals"],
    recommendedCourses: ["Retirement & Investment Accounts", "Advanced Financial Strategy"],
    description: "Invest in fictional stocks with diversification and risk management.",
    phase: 4,
  },
  {
    moduleKey: "tax_strategy",
    moduleName: "Tax Strategy Module",
    route: "/tax-strategy",
    prerequisites: ["Taxes"],
    description: "Learn how different income is taxed and plan tax-aware decisions.",
    phase: 4,
  },
  {
    moduleKey: "wealth_strategy",
    moduleName: "Wealth Builder Simulator",
    route: "/wealth-strategy",
    prerequisites: ["Saving & Budgeting", "Investing Fundamentals", "Career & Income Strategy", "Advanced Financial Strategy", "Real Estate (Homeownership)"],
    description: "Optimize net worth across accounts with tax efficiency and capital deployment.",
    phase: 4,
  },
  {
    moduleKey: "homeownership",
    moduleName: "Real Estate Decision Simulator",
    route: "/homebuying",
    prerequisites: ["Real Estate (Homeownership)"],
    recommendedCourses: ["Insurance & Risk Protection"],
    description: "Walk through buying a home — affordability, mortgage, and equity.",
    phase: 4,
  },
  {
    moduleKey: "re_investing",
    moduleName: "Real Estate Investing Simulator",
    route: "/re-investing",
    prerequisites: ["Real Estate Investing"],
    description: "Evaluate rental deals, balance expenses, and manage returns.",
    phase: 4,
  },
  {
    moduleKey: "development",
    moduleName: "Development Simulator",
    route: "/development",
    prerequisites: ["Real Estate Development"],
    description: "Select development projects, manage costs, timelines, and ROI.",
    phase: 4,
  },
  {
    moduleKey: "entrepreneurship",
    moduleName: "Entrepreneurship Simulator",
    route: "/entrepreneurship",
    prerequisites: ["Business Fundamentals", "Startups & Entrepreneurship", "Business & Startup Expansion"],
    description: "Build and scale a business with pricing, hiring, and growth decisions.",
    phase: 5,
  },
  // Tier 5 — Entrepreneurship Modules
  {
    moduleKey: "opportunity_finder",
    moduleName: "Opportunity Finder Lab",
    route: "/econ/opportunity-finder",
    prerequisites: ["Opportunity Identification"],
    description: "Evaluate business ideas for problem quality, urgency, willingness to pay, and durability.",
    phase: 5,
  },
  {
    moduleKey: "offer_builder",
    moduleName: "Offer Builder Simulator",
    route: "/econ/offer-builder",
    prerequisites: ["Offer Creation & Validation"],
    description: "Create, test, refine, and reposition offers based on customer reactions and feedback.",
    phase: 5,
  },
  {
    moduleKey: "sales_conversation",
    moduleName: "Sales Conversation Simulator",
    route: "/econ/sales-conversation",
    prerequisites: ["Sales & Marketing Fundamentals"],
    description: "Practice trust-based selling, objection handling, and value communication.",
    phase: 5,
  },
  {
    moduleKey: "business_model_sim",
    moduleName: "Business Model Simulator",
    route: "/econ/business-model",
    prerequisites: ["Business Model Design"],
    description: "Construct and stress-test business models using pricing, costs, and retention assumptions.",
    phase: 5,
  },
  {
    moduleKey: "operations_engine",
    moduleName: "Operations Engine Simulator",
    route: "/econ/operations-engine",
    prerequisites: ["Operations & Execution"],
    description: "Simulate fulfillment, staffing, quality control, and workflow design challenges.",
    phase: 5,
  },
  {
    moduleKey: "founder_finance",
    moduleName: "Founder Finance Simulator",
    route: "/econ/founder-finance",
    prerequisites: ["Entrepreneurial Finance"],
    description: "Manage burn rate, runway, working capital, owner pay, and reinvestment decisions.",
    phase: 5,
  },
  {
    moduleKey: "scale_strategy",
    moduleName: "Scale Strategy Simulator",
    route: "/econ/scale-strategy",
    prerequisites: ["Growth & Scale"],
    description: "Simulate hiring, systems growth, channel expansion, and overexpansion risk.",
    phase: 5,
  },
  {
    moduleKey: "founder_decisions",
    moduleName: "Founder Decisions Arena",
    route: "/econ/founder-decisions",
    prerequisites: ["Founder Decision-Making"],
    description: "Scenario-based decision rounds involving pressure, uncertainty, pivoting, and timing.",
    phase: 5,
  },
  {
    moduleKey: "ownership_strategy",
    moduleName: "Ownership Strategy Simulator",
    route: "/econ/ownership-strategy",
    prerequisites: ["Ownership, Equity & Exit Strategy"],
    description: "Simulate dilution, financing structures, control, exits, and long-term ownership tradeoffs.",
    phase: 5,
  },
];

function describeNextCourseStep(courseName: string, lessons: LessonRow[], completedLessons: Set<string>) {
  const courseLessons = lessons
    .filter((lesson) => lesson.course_name === courseName)
    .sort((a, b) => a.lesson_order - b.lesson_order);

  const nextLesson = courseLessons.find((lesson) => !completedLessons.has(lesson.lesson_id));
  if (!nextLesson) return `Complete ${courseName}`;

  return courseLessons.length > 1
    ? `Finish Lesson ${nextLesson.lesson_order} of ${courseName}`
    : `Complete ${courseName}`;
}

function formatUnlockRequirement(requirements: string[]) {
  if (requirements.length === 0) return null;
  if (requirements.length === 1) return `${requirements[0]} to unlock`;
  if (requirements.length === 2) return `${requirements[0]} and ${requirements[1]} to unlock`;
  return `${requirements.slice(0, -1).join(", ")}, and ${requirements[requirements.length - 1]} to unlock`;
}

export function useCurriculumProgress() {
  const { user } = useAuth();
  const [courseProgress, setCourseProgress] = useState<CourseProgress[]>([]);
  const [moduleGates, setModuleGates] = useState<ModuleGate[]>([]);
  const [accountSetupUnlocked, setAccountSetupUnlocked] = useState(false);
  const [accountSetupCompleted, setAccountSetupCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lesson5Completed, setLesson5Completed] = useState(false);

  const fetchProgress = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const [{ data: lessons }, { data: progress }, { data: moduleCompletions }, { data: readinessRows }] = await Promise.all([
      supabase.from("lessons").select("lesson_id, course_name, lesson_order"),
      supabase.from("user_progress").select("lesson_id, completed").eq("user_id", user.id).eq("completed", true),
      supabase.from("module_completions").select("module_key, passed").eq("user_id", user.id).eq("passed", true),
      supabase.from("module_definitions").select("module_key, readiness"),
    ]);

    const readinessMap = new Map<string, ModuleReadiness>();
    (readinessRows || []).forEach((row: AnyRow) => {
      if (row?.module_key && row?.readiness) {
        readinessMap.set(row.module_key, row.readiness as ModuleReadiness);
      }
    });

    const lessonRows = (lessons || []) as LessonRow[];
    const completedLessonIds = new Set((progress || []).map((item) => item.lesson_id));

    const courseMap = new Map<string, { total: number; completed: number; minOrder: number; maxOrder: number }>();
    lessonRows.forEach((lesson) => {
      const entry = courseMap.get(lesson.course_name) || { total: 0, completed: 0, minOrder: 999, maxOrder: 0 };
      entry.total += 1;
      if (completedLessonIds.has(lesson.lesson_id)) entry.completed += 1;
      entry.minOrder = Math.min(entry.minOrder, lesson.lesson_order);
      entry.maxOrder = Math.max(entry.maxOrder, lesson.lesson_order);
      courseMap.set(lesson.course_name, entry);
    });

    const courses: CourseProgress[] = [];
    courseMap.forEach((value, courseName) => {
      if (HIDDEN_COURSES.has(courseName)) return;
      const phaseMeta = PHASE_MAP[courseName] || { phase: 5, label: "Advanced Systems" };
      courses.push({
        courseName,
        totalLessons: value.total,
        completedLessons: value.completed,
        isComplete: value.completed >= value.total,
        phase: phaseMeta.phase,
        phaseLabel: phaseMeta.label,
        minOrder: value.minOrder,
        maxOrder: value.maxOrder,
      });
    });
    courses.sort((a, b) => a.minOrder - b.minOrder);
    setCourseProgress(courses);

    const completedCourses = new Set(courses.filter((course) => course.isComplete).map((course) => course.courseName));
    const completedModuleKeys = new Set<string>((moduleCompletions || []).map((completion: AnyRow) => completion.module_key));

    const financialFoundationIntro = lessonRows
      .filter((lesson) => lesson.course_name === "Financial Foundations" && lesson.lesson_order <= 5)
      .sort((a, b) => a.lesson_order - b.lesson_order);
    const has5 = financialFoundationIntro.every((lesson) => completedLessonIds.has(lesson.lesson_id));
    setLesson5Completed(has5);

    const localAccountSetupDone = localStorage.getItem(`account_setup_${user.id}`) === "true";
    const serverAccountSetupDone = completedModuleKeys.has("account_setup");
    // Server is the source of truth. If a reset wiped the completion row,
    // also clear the stale local flag so the user is sent through onboarding.
    if (localAccountSetupDone && !serverAccountSetupDone) {
      localStorage.removeItem(`account_setup_${user.id}`);
    }
    const setupDone = serverAccountSetupDone;

    if (setupDone) {
      completedModuleKeys.add("account_setup");
    }

    setAccountSetupCompleted(setupDone);
    setAccountSetupUnlocked(has5 || setupDone);

    const gates: ModuleGate[] = MODULE_DEFINITIONS
      // Drop hidden modules entirely. coming_soon and beta still surface,
      // but ModuleUnlockCard renders them with a "Coming Soon" / "Beta" treatment.
      .filter((definition) => readinessMap.get(definition.moduleKey) !== "hidden")
      .map((definition) => {
        const readiness: ModuleReadiness = readinessMap.get(definition.moduleKey) ?? "live";
        const isCompleted = completedModuleKeys.has(definition.moduleKey);

        if (definition.moduleKey === "account_setup") {
          const missingPrereqs = has5
            ? []
            : [
                financialFoundationIntro.find((lesson) => !completedLessonIds.has(lesson.lesson_id))
                  ? `Finish Lesson ${financialFoundationIntro.find((lesson) => !completedLessonIds.has(lesson.lesson_id))!.lesson_order} of Financial Foundations`
                  : "Complete the first 5 Financial Foundations lessons",
              ];

          return {
            ...definition,
            isCompleted,
            // coming_soon modules are never enterable even if prereqs met
            isUnlocked: readiness === "coming_soon" ? false : (has5 || isCompleted),
            missingPrereqs,
            unlockRequirement: readiness === "coming_soon"
              ? "Coming Soon — full simulator in development"
              : formatUnlockRequirement(missingPrereqs),
            readiness,
          };
        }

        const missingCourses = definition.prerequisites.filter((courseName) => !completedCourses.has(courseName));
        const missingPrereqs = missingCourses.map((courseName) => describeNextCourseStep(courseName, lessonRows, completedLessonIds));

        return {
          ...definition,
          isCompleted,
          isUnlocked: readiness === "coming_soon" ? false : (missingCourses.length === 0 || isCompleted),
          missingPrereqs,
          unlockRequirement: readiness === "coming_soon"
            ? "Coming Soon — full simulator in development"
            : formatUnlockRequirement(missingPrereqs),
          readiness,
        };
      });

    setModuleGates(gates);

    if (completedCourses.has("Credit & Debt Management")) {
      void supabase.rpc("unlock_credit_account");
    }
    if (completedCourses.has("Retirement & Investment Accounts")) {
      void Promise.all([
        supabase.rpc("unlock_user_account", { p_account_type: "roth_ira" }),
        supabase.rpc("unlock_user_account", { p_account_type: "401k" }),
        supabase.rpc("unlock_user_account", { p_account_type: "investment" }),
      ]);
    }

    setLoading(false);
  }, [user]);

  useEffect(() => {
    void fetchProgress();
  }, [fetchProgress]);

  const completeAccountSetup = useCallback(async () => {
    if (!user) return;

    localStorage.setItem(`account_setup_${user.id}`, "true");
    setAccountSetupCompleted(true);
    setAccountSetupUnlocked(true);

    // Record the completion FIRST: unlock_user_account verifies this evidence
    // server-side before activating any account.
    await supabase.from("module_completions").upsert({
      user_id: user.id,
      module_key: "account_setup",
      score: 1,
      max_score: 1,
      passed: true,
      completed_at: new Date().toISOString(),
    }, { onConflict: "user_id,module_key" });

    await Promise.all([
      supabase.rpc("unlock_user_account", { p_account_type: "checking" }),
      supabase.rpc("unlock_user_account", { p_account_type: "savings" }),
      supabase.rpc("unlock_user_account", { p_account_type: "emergency_fund" }),
    ]);

    await fetchProgress();
  }, [fetchProgress, user]);

  const getPhases = useCallback(() => {
    const phaseMap = new Map<number, { label: string; courses: CourseProgress[]; modules: ModuleGate[] }>();

    courseProgress.forEach((course) => {
      if (!phaseMap.has(course.phase)) {
        phaseMap.set(course.phase, { label: course.phaseLabel, courses: [], modules: [] });
      }
      phaseMap.get(course.phase)!.courses.push(course);
    });

    moduleGates.forEach((module) => {
      if (!phaseMap.has(module.phase)) {
        phaseMap.set(module.phase, { label: "", courses: [], modules: [] });
      }
      phaseMap.get(module.phase)!.modules.push(module);
    });

    return Array.from(phaseMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([phaseNumber, data]) => ({ phaseNumber, ...data }));
  }, [courseProgress, moduleGates]);

  return {
    courseProgress,
    moduleGates,
    accountSetupUnlocked,
    accountSetupCompleted,
    lesson5Completed,
    loading,
    completeAccountSetup,
    getPhases,
    refetch: fetchProgress,
  };
}
